import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import {
  type AppearanceMode,
  isGlassAppearance,
  isValidAppearanceMode,
} from '../lib/appearanceModes';

const STORAGE_KEY = 'lexgat-appearance';

function readStoredAppearance(): AppearanceMode {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(STORAGE_KEY);
  return isValidAppearanceMode(stored) ? stored : 'light';
}

function applyAppearanceToDocument(mode: AppearanceMode) {
  document.documentElement.dataset.appearance = mode;
}

export type { AppearanceMode } from '../lib/appearanceModes';

interface AppearanceContextValue {
  appearance: AppearanceMode;
  setAppearance: (mode: AppearanceMode) => void;
  isGlass: boolean;
}

const AppearanceContext = createContext<AppearanceContextValue | undefined>(undefined);

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [appearance, setAppearanceState] = useState<AppearanceMode>(() => readStoredAppearance());

  useEffect(() => {
    applyAppearanceToDocument(appearance);
  }, [appearance]);

  const setAppearance = useCallback((mode: AppearanceMode) => {
    setAppearanceState(mode);
    localStorage.setItem(STORAGE_KEY, mode);
    applyAppearanceToDocument(mode);
  }, []);

  return (
    <AppearanceContext.Provider
      value={{
        appearance,
        setAppearance,
        isGlass: isGlassAppearance(appearance),
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error('useAppearance must be used within AppearanceProvider');
  }
  return ctx;
}

/** Apply stored appearance before React hydrates (call from main entry if needed). */
export function initAppearanceFromStorage() {
  applyAppearanceToDocument(readStoredAppearance());
}
